<script setup lang="ts">
/**
 * « Vous aimerez aussi » — rebond vers les autres produits, en BAS de fiche.
 *
 * ⚠️ POURQUOI UN COMPOSANT DÉDIÉ ET PAS [[BoutiqueProductCard]].
 * `BoutiqueProductCard` est la carte de la GRILLE VITRINE : elle est dense par
 * nécessité (badge catégorie, badge « Téléchargement immédiat », description
 * courte, badge de stock restant avec pulsation temps réel, bouton `+`
 * d'ajout au panier, `@emit('open')`), et elle est encartée — fond blanc,
 * bordure, ombre au survol. Ici la fonction est inverse : on ne vend pas ces
 * produits, on invite à les regarder après avoir lu la fiche en cours. La
 * référence retenue (canon Shopify) est volontairement nue — grande photo,
 * nom, prix, pastilles de couleur — sans carte ni bouton d'achat, pour ne pas
 * concurrencer le bouton d'ajout de la fiche.
 * Réutiliser la carte vitrine aurait exigé quatre ou cinq props de
 * masquage (`hideDescription`, `hideBadges`, `hideAddButton`, `bare`…) plus
 * l'ajout des pastilles : la carte vitrine serait devenue un composant à
 * variantes pour un seul appelant. Les deux blocs restent donc distincts, et
 * la vitrine n'est pas touchée.
 *
 * Le lien est un VRAI `NuxtLink` vers `shopProductPath` : ces fiches ont été
 * créées pour être partageables et indexables. Un bouton JS casserait le
 * clic-molette, « ouvrir dans un nouvel onglet » et le crawl.
 */
import type { RouteLocationRaw } from "vue-router";
import type { ShopProduct, VariantAxis } from "~/types/shop";
import { shopProductPriceLabel, shopProductSoldOut } from "~/utils/shopPricing";
import {
  resolveShopColorSwatch,
  shouldRenderAsColorSwatches,
} from "~/utils/shopColorSwatch";

const props = defineProps<{
  /** Autres produits de la boutique — déjà filtrés et plafonnés par le parent. */
  products: ShopProduct[];
  /** Construit la destination d'un produit (porte `carryQuery` : embed/aperçu). */
  productLink: (product: ShopProduct) => RouteLocationRaw;
  /** Base Directus pour les vignettes. */
  directusUrl: string;
}>();

function thumbnailUrl(product: ShopProduct): string | null {
  const id = product.images?.[0];
  if (!id) return null;
  return `${props.directusUrl}/api/assets/${id}?width=600&height=600&fit=cover&format=webp&quality=80`;
}

/**
 * Pastilles de couleur d'un produit, ou `[]` s'il n'y a rien de SÛR à montrer.
 *
 * Même règle que le sélecteur d'achat (`shopColorSwatch`) : il faut un axe dont
 * le NOM désigne une couleur ET dont TOUTES les valeurs sont reconnues par le
 * dictionnaire. Une seule valeur inconnue → aucune pastille pour ce produit,
 * jamais un rond gris inventé qui laisserait croire à un coloris « gris ».
 */
function colorSwatches(product: ShopProduct): { value: string; hex: string; hex2: string | null }[] {
  const axes: VariantAxis[] = product.variant_axes ?? [];
  const colorAxis = axes.find((axis) => shouldRenderAsColorSwatches(axis));
  if (!colorAxis) return [];
  return colorAxis.values.flatMap((value) => {
    const swatch = resolveShopColorSwatch(value);
    // Non-null par construction (`shouldRenderAsColorSwatches` exige 100 % de
    // valeurs résolues) ; le garde satisfait le typage sans `!` ni `any`.
    return swatch ? [{ value, hex: swatch.hex, hex2: swatch.hex2 }] : [];
  });
}

/**
 * Aplats PLEINS uniquement (règle `ui_no_bg_transparency`) : une bicolore est
 * peinte en deux moitiés via un `linear-gradient` à arrêt franc — deux couleurs
 * opaques, aucun canal alpha.
 */
function swatchStyle(swatch: { hex: string; hex2: string | null }): Record<string, string> {
  return {
    background: swatch.hex2
      ? `linear-gradient(135deg, ${swatch.hex} 0 50%, ${swatch.hex2} 50% 100%)`
      : swatch.hex,
  };
}

function priceLabel(product: ShopProduct): string {
  return shopProductPriceLabel(product);
}

function isSoldOut(product: ShopProduct): boolean {
  return shopProductSoldOut(product);
}
</script>

<template>
  <section
    v-if="products.length > 0"
    aria-labelledby="boutique-related-title"
  >
    <!-- Titre CENTRÉ encadré de filets (canon vitrine). Les filets sont des
         `<div>` décoratifs et non un `<hr>` : ils encadrent le titre, ils ne
         séparent pas deux sections de contenu. -->
    <div class="flex items-center gap-4">
      <div class="h-px flex-1 bg-gray-200 dark:bg-[#262626]" />
      <h2
        id="boutique-related-title"
        class="text-lg md:text-xl font-bold tracking-tight text-[#2d2e5c] dark:text-white text-center"
      >
        {{ $t("boutique.relatedProducts.titre") }}
      </h2>
      <div class="h-px flex-1 bg-gray-200 dark:bg-[#262626]" />
    </div>

    <!-- 2 colonnes en mobile (une seule ferait défiler très longtemps pour
         3 produits), jusqu'à 4 en desktop. -->
    <div class="grid grid-cols-2 md:grid-cols-3 wide:grid-cols-4 gap-4 md:gap-6 mt-6 md:mt-8">
      <NuxtLink
        v-for="other in products"
        :key="other.id"
        :to="productLink(other)"
        class="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 rounded-2xl"
      >
        <div class="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 dark:bg-[#1a1a1a]">
          <img
            v-if="thumbnailUrl(other)"
            :src="thumbnailUrl(other) ?? undefined"
            :alt="other.name"
            loading="lazy"
            class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
          <div v-else class="w-full h-full flex items-center justify-center">
            <UIcon name="i-lucide-package" class="size-10 text-gray-300 dark:text-gray-600" />
          </div>

          <!-- Badge TEXTUEL (jamais un signal seulement visuel). -->
          <span
            v-if="isSoldOut(other)"
            class="absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-300"
          >
            {{ $t("boutique.commun.epuise") }}
          </span>
        </div>

        <p class="text-sm md:text-base font-semibold text-[#2d2e5c] dark:text-white line-clamp-2 mt-3">
          {{ other.name }}
        </p>
        <p
          class="text-sm font-bold mt-1"
          :class="priceLabel(other) === $t('boutique.commun.gratuit')
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-[#505870] dark:text-gray-300'"
        >
          {{ priceLabel(other) }}
        </p>

        <!-- Coloris disponibles. Rien n'est affiché quand l'axe couleur est
             absent ou partiellement inconnu : pas d'équivalent inventé au
             « Available in 8 styles » de la référence. La liste est décorative
             ici (le choix réel se fait sur la fiche), d'où l'`aria-hidden` et
             le libellé textuel qui la précède pour les lecteurs d'écran. -->
        <template v-if="colorSwatches(other).length > 0">
          <span class="sr-only">
            {{ $t("boutique.commun.colorisDisponibles", { liste: colorSwatches(other).map((s) => s.value).join(', ') }) }}
          </span>
          <div class="flex items-center gap-1.5 flex-wrap mt-2" aria-hidden="true">
            <span
              v-for="swatch in colorSwatches(other)"
              :key="swatch.value"
              :title="swatch.value"
              class="size-4 rounded-full border border-gray-300 dark:border-[#3a3a3a]"
              :style="swatchStyle(swatch)"
            />
          </div>
        </template>
      </NuxtLink>
    </div>
  </section>
</template>
